import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteStop } from '../salesRepDashboardTypes';
import styles from './RouteOptimizationCard.module.css';

interface RouteOptimizationCardProps {
  stops: RouteStop[];
}

/**
 * Real data, not a fixture: `stops` is built (in SalesRepDashboard) from the
 * rep's own sales_activities scheduled for today. There's no drive-time or
 * distance data available (the dedicated route endpoint is a pre-existing
 * broken 500 — see salesRepDashboardTypes.ts), so instead of fabricating an
 * ETA/mileage we show what's real: sequence ("Stop 2 of 4") and the
 * customer's actual last-order info from their metrics.
 */
export const RouteOptimizationCard: React.FC<RouteOptimizationCardProps> = ({ stops }) => {
  const navigate = useNavigate();
  const completed = stops.filter((s) => s.state === 'done').length;
  const next = stops.find((s) => s.state === 'active') ?? stops.find((s) => s.state === 'pending') ?? null;

  return (
    <div className={styles.card}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>Today's Visit Route</h3>
        <div className={styles.headerActions}>
          <button type="button" className={styles.linkBtn} onClick={() => navigate('/orders')}>
            + View existing orders
          </button>
          <button type="button" className={styles.optimizeBtn} onClick={() => navigate('/log-activity')}>
            Log a Visit
          </button>
        </div>
      </div>

      {stops.length === 0 ? (
        <p className={styles.subtitle}>
          No store visits scheduled for today yet. Log a Visit activity with today's follow-up date to build your route.
        </p>
      ) : (
        <>
          <p className={styles.subtitle}>
            {completed} of {stops.length} stop{stops.length === 1 ? '' : 's'} completed today
          </p>

          <div className={styles.stops}>
            {stops.map((s, idx) => (
              <React.Fragment key={s.id}>
                <span
                  className={`${styles.stopDot} ${styles[`state_${s.state}`]}`}
                  title={`${s.name} · ${s.timeLabel}`}
                >
                  {s.state === 'done' ? '✓' : s.sequence}
                </span>
                {idx < stops.length - 1 && (
                  <span className={`${styles.line} ${s.state === 'done' ? styles.lineDone : ''}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={styles.body}>
            <div className={styles.mapBox}>
              <span className={styles.mapLabel}>Route Map · Coming Soon</span>
            </div>
            <div className={styles.next}>
              {next ? (
                <>
                  <h4 className={styles.nextTitle}>Next Stop</h4>
                  <div className={styles.nextCard}>
                    <div className={styles.nextHeader}>
                      <span className={styles.nextDot} />
                      <span className={styles.nextName}>{next.name}</span>
                    </div>
                    {next.address && <p className={styles.nextMeta}>📍 {next.address}</p>}
                    <p className={styles.nextMeta}>
                      ⏰ {next.timeLabel} · Stop {next.sequence} of {stops.length}
                    </p>
                    <p className={styles.nextMeta}>
                      {next.lastOrder
                        ? `🧾 Last order: $${next.lastOrder.amount.toFixed(2)} · ${new Date(next.lastOrder.date).toLocaleDateString()}`
                        : '🧾 No previous orders on file'}
                    </p>
                    <button type="button" className={styles.startBtn} onClick={() => navigate('/log-activity')}>
                      Start Visit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h4 className={styles.nextTitle}>All Caught Up</h4>
                  <div className={styles.nextCard}>
                    <p className={styles.nextMeta}>Every stop scheduled for today is complete. 🎉</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
